import React, { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import {
  MessageSquare, ShoppingBag, ScanLine, Pill, Shield, LogOut, LayoutDashboard,
  FolderOpen, Plane, ClipboardPlus, Mail, KeyRound, Send, Home,
} from "lucide-react";
import { LOGO_URL, BRAND, BRAND_ORANGE } from "../lib/brand";
import ReferralPadModal from "./ReferralPadModal";
import PadRequestModal from "./PadRequestModal";

const navItems = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  // GCOMS.ai is temporarily hidden from end users while reasoning is
  // being seeded. Route + page intentionally kept so admins can still
  // reach it directly and we don't lose the implementation.
  // { to: "/yunov", label: "GCOMS.ai", icon: MessageSquare, accent: "text-sage-deep", testid: "nav-yunov" },
  { to: "/coverage", label: "Coverage", icon: Plane, accent: "text-gcoms", testid: "nav-coverage" },
  { to: "/doc-center", label: "Doc.Center", icon: FolderOpen, accent: "text-gcoms", testid: "nav-doccenter" },
  { to: "/drugs", label: "Drugs.rx", icon: Pill, accent: "text-moss-deep", testid: "nav-drugs" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [referOpen, setReferOpen] = useState(false);
  const [padsOpen, setPadsOpen] = useState(false);

  return (
    <div className="min-h-screen flex bg-bone">
      <ReferralPadModal open={referOpen} onClose={() => setReferOpen(false)} />
      <PadRequestModal open={padsOpen} onClose={() => setPadsOpen(false)} />

      <aside className="hidden md:flex md:flex-col w-64 border-r border-black/5 bg-white p-6">
        <Link to="/dashboard" className="mb-10 block" data-testid="sidebar-brand">
          <div className="flex items-center gap-2.5">
            <img src={LOGO_URL} alt="GCOMS" className="w-9 h-9 rounded-full bg-black p-0.5 shrink-0" />
            <div>
              <div className="font-heading text-lg font-bold tracking-tight leading-none">{BRAND}</div>
              <div className="uppercase-label mt-1">Referral Network</div>
            </div>
          </div>
        </Link>
        <nav className="flex flex-col gap-1 flex-1">
          {navItems.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={n.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-all ${
                  isActive ? "bg-bone text-ink font-medium" : "text-muted-foreground hover:text-ink hover:bg-bone"
                }`
              }
            >
              <n.icon size={18} className={n.accent || ""} />
              <span>{n.label}</span>
            </NavLink>
          ))}
          {user?.is_admin && (
            <>
              <NavLink
                to="/admin"
                data-testid="nav-admin"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-all mt-4 ${
                    isActive ? "bg-bone text-ink font-medium" : "text-muted-foreground hover:text-ink hover:bg-bone"
                  }`
                }
              >
                <Shield size={18} />
                <span>Admin</span>
              </NavLink>
              <NavLink
                to="/referrals"
                data-testid="nav-referrals"
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-all ${
                    isActive ? "bg-bone text-ink font-medium" : "text-muted-foreground hover:text-ink hover:bg-bone"
                  }`
                }
              >
                <Send size={18} />
                <span>Referral Sender</span>
              </NavLink>
            </>
          )}
          {/* Opt-out: lets logged-in users view the public marketing page
              without the auto-redirect (?home=1 bypasses the Landing
              gate). Sits below the main nav so it doesn't compete with
              core app destinations. */}
          <Link
            to="/?home=1"
            data-testid="nav-public-site"
            className="mt-6 flex items-center gap-3 px-3 py-2 rounded-md text-sm text-muted-foreground hover:text-ink hover:bg-bone transition-all"
          >
            <Home size={18} />
            <span>View public site</span>
          </Link>
        </nav>
        <div className="mt-8 pt-6 border-t border-black/5">
          <div className="text-sm font-medium text-ink truncate" data-testid="sidebar-username">{user?.name}</div>
          <div className="text-xs text-muted-foreground truncate">{user?.email}</div>
          <NavLink
            to="/change-password"
            data-testid="nav-change-password"
            className="mt-4 flex items-center gap-2 text-xs text-muted-foreground hover:text-ink transition-colors"
          >
            <KeyRound size={13} /> Change password
          </NavLink>
          <button
            data-testid="logout-button"
            onClick={logout}
            className="mt-2 flex items-center gap-2 text-xs text-muted-foreground hover:text-ink transition-colors"
          >
            <LogOut size={14} /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 glass border-b border-black/5 px-4 py-3 flex items-center justify-between">
        <Link to="/dashboard" className="flex items-center gap-2 font-heading font-bold">
          <img src={LOGO_URL} alt="GCOMS" className="w-7 h-7 rounded-full bg-black p-0.5" />
          {BRAND}
        </Link>
        <button onClick={logout} data-testid="mobile-logout-button" className="text-xs text-muted-foreground">Sign out</button>
      </div>

      <main className="flex-1 min-w-0">
        <div className="md:hidden h-14" />

        {/* Persistent Refer-a-Patient ribbon — visible on every authenticated page */}
        <div
          data-testid="refer-ribbon"
          className="sticky top-0 z-20 border-b border-black/5"
          style={{ backgroundColor: "#FDE6D8" }}
        >
          <div className="px-6 lg:px-10 py-2.5 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2.5 min-w-0">
              <ClipboardPlus size={16} style={{ color: BRAND_ORANGE }} className="shrink-0" />
              <span className="text-sm text-ink/80 truncate">
                <span className="font-medium text-ink">Refer a patient</span>
                <span className="hidden sm:inline"> — routed to <span className="font-mono">info@goldcoastoms.com</span></span>
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                data-testid="hero-refer-patient"
                onClick={() => setReferOpen(true)}
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-md text-white text-xs font-medium hover:opacity-90"
                style={{ backgroundColor: BRAND_ORANGE }}
              >
                <ClipboardPlus size={13} /> Refer a patient
              </button>
              <button
                data-testid="hero-request-pads"
                onClick={() => setPadsOpen(true)}
                className="inline-flex items-center gap-1.5 px-3.5 py-1.5 rounded-md border text-xs font-medium hover:bg-white"
                style={{ borderColor: BRAND_ORANGE, color: BRAND_ORANGE }}
              >
                <Mail size={13} /> Request pads
              </button>
            </div>
          </div>
        </div>

        {children}
      </main>
    </div>
  );
}
